/* useAddressSync — mount in a page whose record lives in page state
   (project dashboard, task panel, invoice, client, person…).

     address   the open record's address, or null while the list shows
     listPath  the list's path ('/projects') — Back lands here
     onLeave   Back landed on the list while a record was open: close it
     onOpenId  optional: the location became `<listPath>/<id>` (typed
               address, Forward, new window) and this page has no deep-link
               consumer of its own — open that record
     title     optional record name for the tab title                     */

import { useEffect, useRef } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router';
import {
  planAddressChange,
  recordIdAt,
  reportAddress,
  setRecordTitle,
  shouldLeaveRecord,
} from './addressSync';

export interface AddressSyncOptions {
  address: string | null;
  listPath: string;
  onLeave: () => void;
  onOpenId?: (id: string) => void;
  title?: string | null;
  /** Off while a host embeds the page (e.g. Tasks inside a project). */
  disabled?: boolean;
  /** Dirty-record guard: Back (a move to the bare list) while `when` is true
   *  hands the page the same prompt its own Back button shows. Call
   *  `proceed` to let the move through, `reset` to stay. */
  guard?: { when: boolean; onBlocked: (proceed: () => void, reset: () => void) => void };
}

export function useAddressSync({
  address,
  listPath,
  onLeave,
  onOpenId,
  title,
  disabled,
  guard,
}: AddressSyncOptions): void {
  const navigate = useNavigate();
  const guardRef = useRef(guard);
  guardRef.current = guard;
  const blocker = useBlocker(
    ({ nextLocation }) =>
      !disabled &&
      !!guardRef.current?.when &&
      shouldLeaveRecord(addressRef.current, nextLocation.pathname + nextLocation.search, listPath)
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    guardRef.current?.onBlocked(blocker.proceed, blocker.reset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);
  const loc = useLocation();
  const here = loc.pathname + loc.search;
  const hereRef = useRef(here);
  hereRef.current = here;
  const prevRef = useRef<string | null>(null);
  const leaveRef = useRef(onLeave);
  leaveRef.current = onLeave;
  const openRef = useRef(onOpenId);
  openRef.current = onOpenId;
  const addressRef = useRef(address);
  addressRef.current = address;

  // Page state -> address.
  useEffect(() => {
    if (disabled) return;
    const plan = planAddressChange(prevRef.current, address, hereRef.current, listPath);
    prevRef.current = address;
    if (plan.action === 'none') return;
    reportAddress(plan.to);
    void navigate(plan.to, { replace: plan.action === 'replace' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, disabled, listPath]);

  // Address -> page state (Back / Forward / typed address).
  useEffect(() => {
    if (disabled) return;
    if (shouldLeaveRecord(addressRef.current, here, listPath)) {
      leaveRef.current();
      return;
    }
    const id = recordIdAt(here, listPath);
    if (id !== null && openRef.current && here !== addressRef.current) openRef.current(id);
  }, [here, disabled, listPath]);

  // Tab title.
  useEffect(() => {
    if (disabled) return;
    setRecordTitle(address ? (title ?? null) : null);
    return () => setRecordTitle(null);
  }, [address, title, disabled]);
}
